import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ADDED_STATE_DURATION = 1600;

/** The theme declares `Theme` with const in a classic script, so it is global but not on window */
function themeRoute(name, fallback) {
  try {
    return Theme.routes[name] || fallback;
  } catch (error) {
    return fallback;
  }
}

/**
 * Quick Add micro-drawer for the homepage Best Sellers cards.
 * Lets shoppers pick a variant and add it to the cart without leaving the page.
 */
class HomeQuickAdd extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {number | undefined} */
  #addedTimeout;

  connectedCallback() {
    this.trigger = this.querySelector('.home-quick-add__trigger');
    this.drawer = this.querySelector('.home-quick-add__drawer');
    this.form = this.querySelector('.home-quick-add__form');
    this.idInput = this.form?.querySelector('input[name="id"]');
    this.submitButton = this.form?.querySelector('.home-quick-add__submit');
    this.submitLabel = this.form?.querySelector('.home-quick-add__submit-label');
    this.submitPrice = this.form?.querySelector('.home-quick-add__submit-price');
    this.errorMessage = this.form?.querySelector('.home-quick-add__error');
    this.variants = this.#readJson('[data-variants]') || [];
    this.labels = this.#readJson('[data-labels]') || {};

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.trigger?.addEventListener('click', this.#onTriggerClick, { signal });
    this.querySelector('.home-quick-add__close')?.addEventListener('click', () => this.close(), { signal });
    this.form?.addEventListener('change', this.#onOptionChange, { signal });
    this.form?.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    // Theme product cards navigate on any click inside them, so keep drawer clicks to ourselves
    this.addEventListener('click', (event) => event.stopPropagation(), { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });

    this.#updateVariant();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    clearTimeout(this.#addedTimeout);
  }

  get #sourceId() {
    return this.drawer?.id || `home-quick-add-${this.dataset.productId}`;
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    // Only one drawer open at a time
    document.querySelectorAll('home-quick-add.is-open').forEach((other) => {
      if (other !== this && other instanceof HomeQuickAdd) other.close({ restoreFocus: false });
    });

    this.drawer?.removeAttribute('inert');
    this.classList.add('is-open');
    this.trigger?.setAttribute('aria-expanded', 'true');

    const firstInput = this.form?.querySelector('input[type="radio"]:checked, input[type="radio"], .home-quick-add__submit');
    if (firstInput instanceof HTMLElement) firstInput.focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;

    const hadFocus = this.drawer?.contains(document.activeElement);
    this.classList.remove('is-open');
    this.drawer?.setAttribute('inert', '');
    this.trigger?.setAttribute('aria-expanded', 'false');

    if (restoreFocus && hadFocus) this.trigger?.focus({ preventScroll: true });
  }

  #onTriggerClick = (event) => {
    event.preventDefault();

    // Products without options skip the drawer and add straight away
    if (this.hasAttribute('data-single-variant')) {
      this.#addToCart();
      return;
    }

    this.isOpen ? this.close() : this.open();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.close();
    }
  };

  #onDocumentClick = (event) => {
    if (this.isOpen && !this.contains(event.target)) this.close({ restoreFocus: false });
  };

  #onOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;

    const legendValue = event.target.closest('fieldset')?.querySelector('.home-quick-add__legend-value');
    if (legendValue) legendValue.textContent = event.target.value;

    this.#updateVariant();
  };

  #onSubmit = (event) => {
    event.preventDefault();
    this.#addToCart();
  };

  /** Selected option values, in option order */
  #selectedOptions() {
    const fieldsets = this.form?.querySelectorAll('.home-quick-add__option') || [];
    return Array.from(fieldsets, (fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : null;
    });
  }

  #updateVariant() {
    if (!this.form || this.hasAttribute('data-single-variant')) return;

    const selected = this.#selectedOptions();
    const variant = this.variants.find((candidate) =>
      candidate.options.every((value, index) => value === selected[index])
    );

    this.#markUnavailableValues(selected);

    if (this.idInput instanceof HTMLInputElement) this.idInput.value = variant ? String(variant.id) : '';
    if (this.submitPrice && variant) this.submitPrice.textContent = variant.price;

    if (!variant) {
      this.#setSubmitState(this.labels.unavailable, true);
    } else if (!variant.available) {
      this.#setSubmitState(this.labels.soldOut, true);
    } else {
      this.#setSubmitState(this.labels.addToCart, false);
    }
  }

  /** Strike through values that have no available variant with the other current selections */
  #markUnavailableValues(selected) {
    this.form?.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const optionIndex = Number(input.dataset.optionIndex);
      const isAvailable = this.variants.some(
        (variant) =>
          variant.available &&
          variant.options.every((value, index) =>
            index === optionIndex ? value === input.value : value === selected[index]
          )
      );
      input.closest('.home-quick-add__value')?.classList.toggle('is-unavailable', !isAvailable);
    });
  }

  #setSubmitState(label, disabled) {
    if (this.submitLabel && label) this.submitLabel.textContent = label;
    if (this.submitButton instanceof HTMLButtonElement) this.submitButton.disabled = disabled;
  }

  async #addToCart() {
    const variantId = this.idInput instanceof HTMLInputElement ? this.idInput.value : '';
    if (!variantId || this.classList.contains('is-loading')) return;

    this.#showError('');
    this.classList.remove('is-added');
    this.classList.add('is-loading');
    this.#setButtonsLabel(this.labels.adding);

    // Ask for the cart drawer's sections so it re-renders with the new item
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'), (item) =>
      item instanceof HTMLElement ? item.dataset.sectionId : ''
    ).filter(Boolean);

    try {
      const response = await fetch(themeRoute('cart_add_url', '/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || this.labels.error;
        this.dispatchEvent(new CartErrorEvent(this.#sourceId, data.message, data.description, data.errors));
        this.#fail(message);
        return;
      }

      const cart = await fetch(`${themeRoute('cart_url', '/cart')}.js`, { headers: { Accept: 'application/json' } })
        .then((cartResponse) => cartResponse.json())
        .catch(() => undefined);

      this.dispatchEvent(
        new CartAddEvent(cart, this.#sourceId, {
          source: 'home-quick-add',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );

      this.#succeed();
    } catch (error) {
      this.#fail(this.labels.error);
    }
  }

  #succeed() {
    this.classList.remove('is-loading');
    this.classList.add('is-added');
    this.#setButtonsLabel(this.labels.added);

    // Slide the cart drawer in right away so shoppers see the item and can check out
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      this.close({ restoreFocus: false });
      cartDrawer.open();
    }

    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => {
      this.classList.remove('is-added');
      this.close({ restoreFocus: false });
      this.#resetLabels();
    }, ADDED_STATE_DURATION);
  }

  #fail(message) {
    this.classList.remove('is-loading');
    this.#resetLabels();

    if (this.hasAttribute('data-single-variant')) {
      // No drawer to show the message in, so open it with just the error and button
      this.open();
    }
    this.#showError(message);
  }

  #setButtonsLabel(label) {
    if (!label) return;
    if (this.submitLabel) this.submitLabel.textContent = label;
    if (this.hasAttribute('data-single-variant') && this.trigger) {
      this.trigger.dataset.originalLabel ??= this.trigger.innerHTML;
      this.trigger.textContent = label;
    }
  }

  #resetLabels() {
    if (this.trigger?.dataset.originalLabel) {
      this.trigger.innerHTML = this.trigger.dataset.originalLabel;
      delete this.trigger.dataset.originalLabel;
    }
    if (this.hasAttribute('data-single-variant')) {
      this.#setSubmitState(this.labels.addToCart, false);
    } else {
      this.#updateVariant();
    }
  }

  #showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = !message;
  }

  #readJson(selector) {
    const script = this.querySelector(selector);
    try {
      return script ? JSON.parse(script.textContent || '') : undefined;
    } catch (error) {
      return undefined;
    }
  }
}

if (!customElements.get('home-quick-add')) {
  customElements.define('home-quick-add', HomeQuickAdd);
}
